import { Box, Card, CardActionArea, CardContent, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { fetchPeopleByHandles, personExtended, restDateToJsDate, restPersonName, type RestPerson } from '../api/grampsRest';
import { fetchLandmarks } from '../api/landmarks';

function lifeYears(person: RestPerson): string {
    const events = personExtended(person).events ?? [];
    const year = (type: string) => restDateToJsDate(events.find((event) => event.type === type)?.date)?.getFullYear();
    const birth = year('Birth');
    const death = year('Death');
    if (!birth && !death) return '';
    return `${birth ?? '?'} – ${death ?? ''}`.trim();
}

/** Where to start when no one is chosen yet: the people with both many
 * ancestors and many descendants, the hinges a family knows. */
export function Landmarks({ treeName, profileId, token, onChoose }: {
    treeName: string | null;
    profileId: string | null;
    token: string | undefined;
    onChoose: (grampsId: string) => void;
}) {
    const landmarksQuery = useQuery({
        queryKey: ['landmarks', treeName, profileId, token],
        queryFn: () => fetchLandmarks(token),
        // Computed once a day on the server.
        staleTime: 60 * 60 * 1000,
        retry: false,
    });
    const handles = useMemo(() => (landmarksQuery.data ?? []).map((landmark) => landmark.handle), [landmarksQuery.data]);
    const peopleQuery = useQuery({
        queryKey: ['people', 'landmarks', treeName, profileId, handles],
        queryFn: () => fetchPeopleByHandles(handles, token),
        enabled: handles.length > 0,
    });
    const people = useMemo(() => new Map((peopleQuery.data ?? []).map((person) => [person.handle, person])), [peopleQuery.data]);
    // Only those the visitor can see, in the server's order.
    const shown = (landmarksQuery.data ?? []).flatMap((landmark) => {
        const person = people.get(landmark.handle);
        return person ? [{ landmark, person }] : [];
    });
    if (shown.length === 0) return null;

    return <Box sx={{ mt: 3, maxWidth: 900 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Par où commencer ?</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Des personnes au carrefour de l'arbre, avec beaucoup d'ancêtres et beaucoup de descendants.
        </Typography>
        <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 150px), 1fr))' }}>
            {shown.map(({ landmark, person }) => <Card key={landmark.handle} variant="outlined">
                <CardActionArea onClick={() => onChoose(landmark.gramps_id)} sx={{ height: '100%' }}>
                    <CardContent sx={{ py: 1.5 }}>
                        <Typography sx={{ fontWeight: 600, color: 'primary.main' }}>{restPersonName(person)}</Typography>
                        <Typography variant="body2" color="text.secondary">{lifeYears(person)}</Typography>
                        <Typography variant="caption" color="text.secondary">
                            {landmark.ancestors} ancêtres · {landmark.descendants} descendants
                        </Typography>
                    </CardContent>
                </CardActionArea>
            </Card>)}
        </Box>
    </Box>;
}
